import {
  Box,
  Button,
  Checkbox,
  HStack,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import InputPhone from "@raiden/library/components/Form/InputPhone";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import FormObserver from "@raiden/library/components/ReactHookForm/FormObserver";
import FormStateRHF from "@raiden/library/components/ReactHookForm/FormStateRHF";
import { RGrid } from "@raiden/library/components/RGrid";
import { useApi } from "@raiden/library/hooks/useApi";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import { ContractsPicker } from "../../../components/Form/ContractsPicker";
import {
  CONTACT_TIME_OF_DAY_LIST,
  CONTACT_TYPES_LIST,
} from "../../../constants/contact";

// using dayjs
// if day isn't  sunday, add it to the list of available dates
function getNext7Days() {
  /** @type {dayjs.Dayjs[]} */
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = dayjs().add(i, "day");
    if (date.day() !== 0) {
      dates.push(date);
    }
  }
  return dates;
}

const AVAILABLE_DATES = getNext7Days();

/**
 * @typedef {Object} Props
 * @property {import("react-hook-form").Control<import("./Handler").ContactFormValues>} control
 * @property {boolean} isLoading
 */

/**
 * @param {Props} props
 */
export function ContactsCreateForm({ control, isLoading }) {
  const intl = useIntl();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Country").Country[]>} */
  const { swrResponse } = useApi(
    generateApiUrl({ id: "@api.countries.viewAny", query: { per_page: 250 } }),
  );

  return (
    <Stack spacing="1.5rem">
      <Stack spacing=".5rem" mt="1.5rem">
        <ApiErrorMessage withCaughtErrors={true} />

        <Text variant="text-medium">
          <FormattedMessage defaultMessage="Choisissez les contrats que vous souhaitez optimiser" />
        </Text>

        <FormController
          control={control}
          name="data.custom_attributes.contracts"
          rules={{ required: true }}
          render={(field) => (
            <ContractsPicker value={field.value} onChange={field.onChange} />
          )}
        />
      </Stack>

      <RGrid minCellWidth="300px" gridGap="1.5rem">
        <FormController
          control={control}
          name="data.first_name"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Prénom" })}
        />

        <FormController
          control={control}
          name="data.last_name"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Nom" })}
        />
      </RGrid>

      <RGrid gridGap="1.5rem">
        <InputPhone
          countries={swrResponse.data?.data ?? []}
          nameCountry="data.phone_country"
          nameNumber="data.phone_number"
        />

        <FormController
          control={control}
          name="data.email"
          rules={{ required: true }}
          label={intl.formatMessage({ defaultMessage: "Adresse email" })}
        />
      </RGrid>

      <RGrid minCellWidth="250px">
        <FormController
          control={control}
          name="data.custom_attributes.contactType"
          label={intl.formatMessage({
            defaultMessage: "Vous souhaitez être appelé·e",
          })}
          renderWithFormControl={(field) => (
            <Select
              placeholder={intl.formatMessage({
                defaultMessage: "Choisir une option",
              })}
              {...field}>
              {CONTACT_TYPES_LIST.map((contactType) => (
                <option value={contactType.value} key={contactType.value}>
                  {intl.formatMessage(contactType.label)}
                </option>
              ))}
            </Select>
          )}
        />

        <FormObserver
          control={control}
          name="data.custom_attributes.contactType">
          {(contactType) => (
            <>
              {contactType === "pickDate" && (
                <>
                  <FormController
                    control={control}
                    name="data.custom_attributes.contactDate"
                    label={intl.formatMessage({
                      defaultMessage: "Quand êtes-vous disponible ?",
                    })}
                    renderWithFormControl={(field) => (
                      <Select
                        placeholder={intl.formatMessage({
                          defaultMessage: "Choisir une option",
                        })}
                        {...field}>
                        {AVAILABLE_DATES.map((date, index) => (
                          <option value={date.format("YYYY-MM-DD")} key={index}>
                            {capitalize(
                              intl.formatDate(date.toISOString(), {
                                month: "long",
                                weekday: "long",
                                day: "numeric",
                              }),
                            )}
                          </option>
                        ))}
                      </Select>
                    )}
                  />

                  <FormController
                    control={control}
                    name="data.custom_attributes.contactTimeOfDay"
                    label={intl.formatMessage({
                      defaultMessage: "Quel horaire préférez-vous ?",
                    })}
                    renderWithFormControl={(field) => (
                      <Select
                        placeholder={intl.formatMessage({
                          defaultMessage: "Choisir une option",
                        })}
                        {...field}>
                        {CONTACT_TIME_OF_DAY_LIST.map((contactType) => (
                          <option
                            value={contactType.value}
                            key={contactType.value}>
                            {intl.formatMessage(contactType.label)}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </>
              )}
            </>
          )}
        </FormObserver>
      </RGrid>

      <Box>
        <FormController
          control={control}
          name="data.content"
          rules={{ required: true }}
          type="textarea"
          label={intl.formatMessage({
            defaultMessage: "Informations complémentaires",
          })}
          placeholder={intl.formatMessage({
            defaultMessage: "Parlez-nous de votre projet",
          })}
        />
      </Box>

      <Box>
        <FormController
          control={control}
          name="data.accept"
          rules={{ required: true }}
          render={(field) => (
            <Checkbox {...field} value={undefined}>
              <FormattedMessage defaultMessage="J'accepte que mes coordonnées soient utilisées dans le cadre de mes échanges avec ALL IN ONE." />
            </Checkbox>
          )}
        />
      </Box>

      <HStack justify="flex-end">
        <FormStateRHF
          render={({ isDirty }) => (
            <Button
              type="submit"
              colorScheme="brandPrimary"
              size="lg"
              isLoading={isLoading}
              isDisabled={!isDirty}>
              <FormattedMessage defaultMessage="Être appelé" />
            </Button>
          )}
        />
      </HStack>
    </Stack>
  );
}
